import React from 'react';
import { Modal } from 'components/molecules';
import { GoogleMap } from 'components/core/GoogleMap';
import PropTypes from 'prop-types';
import { compose, withStateHandlers, withHandlers } from 'recompose';
import FacilityCard from 'components/organisms/FacilityCard';
import { SearchInfoWindow } from 'components/search/SearchMap';
import SearchPin from 'components/search/SearchMap/SearchPin';
import './MapView.scss';

export const enhance = compose(
	withStateHandlers(
		() => ({
			facilitySelected: null,
			pinSelected: false, // true only when clicked
		}),
		{
			handleFacilitySelected: () => (facilityId) => ({
				facilitySelected: facilityId,
			}),
			handleFacilityDeselected: () => () => ({
				facilitySelected: null,
			}),
			handlePinSelected: () => () => ({
				pinSelected: true,
			}),
			handlePinDeselected: () => () => ({
				pinSelected: false,
			}),
		},
	),

	withHandlers({
		handleSelect:
			({ handleFacilitySelected, handlePinSelected }) =>
			(facility) => {
				handleFacilitySelected(facility.id);
				handlePinSelected(); // persist selection
			},

		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				handleFacilityDeselected();
				handlePinDeselected();
			},

		handleMouseEnter:
			({ handleFacilitySelected, pinSelected }) =>
			(facility) => {
				if (pinSelected) return; // don't override clicked state
				handleFacilitySelected(facility.id);
			},

		handleMouseLeave:
			({ handleFacilityDeselected, pinSelected }) =>
			() => {
				if (pinSelected) return; // don't remove if clicked
				handleFacilityDeselected();
			},
	}),
);

export function MapView({
	isOpen,
	onClose,
	facility,
	facilities,
	facilitySelected,
	handleMouseEnter,
	handleMouseLeave,
	handleSelect,
	handleDeselect,
}) {
	if (!facility?.location) return null;

	const selectedFacility = facilities.find((f) => f.id === facilitySelected);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Map View"
			bodyClassName="map-view-modal-body"
		>
			{isOpen && (
				<div className="map-view-two">
					<GoogleMap center={facility.location}>
						{(facilities || []).map((facilityItem) => (
							<SearchPin
								key={facilityItem.id}
								facility={facilityItem}
								onClick={() => handleSelect(facilityItem)}
								onMouseEnter={() =>
									handleMouseEnter(facilityItem)
								}
								onMouseLeave={handleMouseLeave}
							/>
						))}

						{selectedFacility && (
							<>
								<FacilityCard
									facility={selectedFacility}
									facilitySelected={facilitySelected}
								/>
								<SearchInfoWindow
									facility={selectedFacility}
									onClose={handleDeselect} // must be used inside SearchInfoWindow
								/>
							</>
						)}
					</GoogleMap>
				</div>
			)}
		</Modal>
	);
}

MapView.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	facility: PropTypes.object,
	facilitySelected: PropTypes.number,
	facilities: PropTypes.arrayOf(PropTypes.object),
	handleMouseEnter: PropTypes.func.isRequired,
	handleMouseLeave: PropTypes.func.isRequired,
	handleSelect: PropTypes.func.isRequired,
	handleDeselect: PropTypes.func.isRequired,
};

MapView.defaultProps = {
	facilities: [],
};

export default enhance(MapView);
